import dayjs from 'dayjs';
import { useCallback } from 'react';

import { stripHtml } from '@/helpers/estimation';
import { sanitizeFilename } from '@/utils/fileSystem';

import type { ExportTable } from './model';

export const ROW_DELIMITER = '\r\n';
export const COLUMN_DELIMITER = ';';

function escapeValueIfNeeded(value: string | number | boolean | null | undefined) {
  if (value === null || value === undefined) {
    return ''; // Handle null or undefined
  }

  // String in quotes so that it's interpreted as text when imported to excel
  if (typeof value === 'string') {
    // Escape double quotes by doubling them
    const escapedValue = value.replace(/"/g, '""');
    // Enclose the entire value in double quotes
    return `"${escapedValue}"`;
  }

  return String(value);
}

export const useCsvExport = () => {
  const convertToCSV = (table: ExportTable) => {
    let str = '';

    // first prepare the header (ignore columns without headerLabel, eg. columns with buttons)
    let line = '';
    for (const column of table.columns.filter((col) => col.headerLabel)) {
      if (line) {
        line += COLUMN_DELIMITER;
      }

      line += column.headerLabel;
    }

    str += line + ROW_DELIMITER;

    for (let i = 0; i < table.rows.length; i++) {
      line = '';

      for (const column of table.columns.filter((col) => col.headerLabel)) {
        if (line) {
          line += COLUMN_DELIMITER;
        }

        const cellData = table.rows[i][column.accessor];
        const isHtmlColumn = column.isHtml === true;
        const dataToAdd = dayjs.isDayjs(cellData)
          ? cellData.toISOString()
          : isHtmlColumn
            ? stripHtml(cellData as string)
            : cellData;

        line += escapeValueIfNeeded(dataToAdd);
      }

      str += line + ROW_DELIMITER;
    }

    return str;
  };

  return useCallback(async (fileName: string, tables: ExportTable[]) => {
    if (tables.length > 1) {
      // EWM-1048: jszip (~94 kB minified) is only needed for the multi-table zip path, so it is
      // fetched here rather than at module scope.
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();

      // Add CSV files to the zip with UTF-8 BOM for proper Excel encoding
      tables.forEach((table, index) => {
        const csvContent = convertToCSV(table);
        // Add UTF-8 BOM for proper Excel encoding (especially for German umlauts: ü, ö, ä)
        const encoder = new TextEncoder();
        const bom = new Uint8Array([0xef, 0xbb, 0xbf]); // UTF-8 BOM
        const csvBytes = encoder.encode(csvContent);
        const csvContentWithBom = new Uint8Array([...bom, ...csvBytes]);
        const csvFileName = sanitizeFilename(`${table.title ?? 'export'}_${index + 1}.csv`);
        zip.file(csvFileName, csvContentWithBom);
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      const fileNameWithExtension = sanitizeFilename(`${fileName}.zip`);
      downloadFile(zipBlob, fileNameWithExtension);
      return;
    }

    if (tables.length === 1) {
      const csvContent = convertToCSV(tables[0]);
      // Add UTF-8 BOM for proper Excel encoding (especially for German umlauts: ü, ö, ä)
      const encoder = new TextEncoder();
      const bom = new Uint8Array([0xef, 0xbb, 0xbf]); // UTF-8 BOM
      const csvBytes = encoder.encode(csvContent);
      const csvData = new Blob([bom, csvBytes], { type: 'text/csv;charset=utf-8' });
      const fileNameWithExtension = sanitizeFilename(`${fileName}.csv`);
      downloadFile(csvData, fileNameWithExtension);
      return;
    }
  }, []);
};

function downloadFile(fileData: Blob, fileNameWithExtension: string) {
  const fileURL = URL.createObjectURL(fileData);
  const link = document.createElement('a');
  link.href = fileURL;
  link.download = fileNameWithExtension;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
